import { IMLesson, IMModelingPrompt } from '../types'
import { createNodeList, paragraphWithHeader } from '../../nodeCreationHelpers'
import parseLessonActivities from './lessonActivities'
import { ordinalTitle, filteredNoteNodes } from './utility'
import {
  createTable,
  createUnorderedList,
  PARAGRAPH_SPACING,
} from '../../html_utility'
import { parseStandardsGroup } from './standards'
import { parseModelingPrompts } from './modeling_prompt'
import { createItemGlossary } from './glossary'
import { practiceProblemsAssessment } from './assessment'
import { centersList } from './center'
import { ISimplifiedNode } from '../../../components/ingestion/IngestedTree'
import { IM360Parser } from './IM360Parser'

export function parseLessons(
  lessons: IMLesson[],
  modelingPrompts: IMModelingPrompt[],
) {
  return lessons.reduce<ISimplifiedNode[]>((acc, l) => {
    return [...acc, parseLesson(l, modelingPrompts)]
  }, [])
}

function parseLesson(lesson: IMLesson, modelingPrompts: IMModelingPrompt[]) {
  return IM360Parser.getInstance().createLessonPlan(
    {
      title: ordinalTitle(lesson),
    },
    lessonChildren(lesson, modelingPrompts),
  )
}

function activityAncillaryContent(lesson: IMLesson) {
  return (
    lesson.activities?.reduce<Record<string, string[]>>(
      (acc, { ancillary_content }) => {
        Object.entries(ancillary_content).forEach(([key, value]) => {
          if (!(key in acc)) {
            acc[key] = []
          }
          acc[key].push(...value.map(({ title }) => title))
        })
        return acc
      },
      {},
    ) ?? {}
  )
}

function materialsList(
  key: 'required_materials' | 'materials_to_copy',
  lesson: IMLesson,
) {
  const lessonMaterials = (lesson.ancillary_content?.[key] ?? []).reduce<
    string[]
  >((acc, { title }) => {
    return [...acc, `${title}: Lesson`]
  }, [])
  const activityMaterials =
    lesson.activities?.reduce<Record<string, string[]>>(
      (acc, { ancillary_content, ordinal_title }) => {
        ancillary_content[key]?.forEach(({ title }) => {
          if (!(title in acc)) {
            acc[title] = []
          }
          acc[title].push(ordinal_title)
        })
        return acc
      },
      {},
    ) ?? {}

  const activitiesMaterialsList = Object.entries(activityMaterials).reduce<
    string[]
  >((acc, [item, locs]) => {
    return [...acc, `${item}: ${locs.join(', ')}`]
  }, [])
  return createUnorderedList([...lessonMaterials, ...activitiesMaterialsList])
}

function preparationList({ required_preparation, activities }: IMLesson) {
  const contentList =
    activities?.reduce<string[]>((acc, { ordinal_title, required_prep }) => {
      if (required_prep) {
        acc.push(paragraphWithHeader(`${ordinal_title}:`, required_prep))
      }
      return acc
    }, []) ?? []
  if (required_preparation) {
    contentList.push(paragraphWithHeader('Lesson:', required_preparation))
  }
  return contentList.join(PARAGRAPH_SPACING)
}

function lessonChildren(lesson: IMLesson, modelingPrompts: IMModelingPrompt[]) {
  const activityContent = activityAncillaryContent(lesson)
  const childrenNotes: [string, string | undefined][] = [
    [
      'Learning Goals',
      lesson.ancillary_content?.goals &&
        createUnorderedList(
          lesson.ancillary_content.goals.map(({ title }) => title).sort(),
        ),
    ],
    ['Student Facing Learning Goals', lesson.student_facing_learning_goals],
    [
      'Learning Targets (Student Facing)',
      lesson.ancillary_content?.learning_targets &&
        createUnorderedList(
          lesson.ancillary_content.learning_targets
            .map(({ title }) => title)
            .sort(),
        ),
    ],
    ['Lesson Purpose', lesson.lesson_purpose],
    ['Lesson Narrative', lesson.narrative],
    [
      'Instructional Routines',
      activityContent.instructional_routines &&
        createUnorderedList(activityContent.instructional_routines),
    ],
    [
      'Access For Students with Disabilities',
      activityContent.swd_supports &&
        createUnorderedList(activityContent.swd_supports),
    ],
    [
      'Access For English Learners',
      activityContent.mathematical_language_routines &&
        createUnorderedList(activityContent.mathematical_language_routines),
    ],
    ['Required Materials', materialsList('required_materials', lesson)],
    ['Materials To Copy', materialsList('materials_to_copy', lesson)],
    ['Required Preparation', preparationList(lesson)],
    ['Lesson Timeline', timeline(lesson)],
    ['Teacher Reflection', lesson.teacher_reflection_questions!],
    ['Suggested Centers', centersList(lesson)],
  ]
  return [
    ...parseStandardsGroup(lesson.standards, lesson.ancillary_content),
    ...filteredNoteNodes(...childrenNotes),
    ...parseLessonActivities(lesson),
    ...practiceProblems(lesson),
    ...parseModelingPrompts(lesson, modelingPrompts),
    ...createItemGlossary(lesson.ancillary_content),
  ]
}

function practiceProblems(lesson: IMLesson) {
  const practiceProblemsNode = practiceProblemsAssessment(lesson)
  if (!practiceProblemsNode.length) return []
  return [createNodeList({ title: 'Practice Problems' }, practiceProblemsNode)]
}

function timeline(lesson: IMLesson) {
  const data = [
    ...(lesson.activities ?? []).map(({ ordinal_title, time_estimate }) => [
      ordinal_title,
      `${time_estimate} min`,
    ]),
    ['Synthesis Estimate', `${lesson.synthesis_estimate} min`],
    ...lesson.assessments.reduce<string[][]>((acc, { type, time_estimate }) => {
      if (!time_estimate) return acc
      return [...acc, [type, `${time_estimate} min`]]
    }, []),
  ]
  return createTable(data)
}
